// landing/sections/HowItWorksSection.tsx — the moderation pipeline as four
// steps a stranger can follow: report → jury → public decision → appeal.
//
// Static content, no backend. Every claim restates what the Council actually
// does (see CouncilSection's receipts links); if the pipeline ever changes,
// this strip changes with it or the landing page is lying.

import styled from 'styled-components';
import { ConsolePanel } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  counter-reset: step;
`;

const Step = styled(ConsolePanel).attrs({ as: 'li' })`
  counter-increment: step;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;

  /* "Step 1", not "STEP 01": a counter in the body face, muted, untracked. */
  &::before {
    content: 'Step ' counter(step);
    font-family: ${({ theme }) => theme.font.body};
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const StepTitle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  svg {
    color: ${({ theme }) => theme.color.accentCyan};
    flex-shrink: 0;
  }
`;

const StepBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

const STEPS: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'flag',
    title: 'Someone reports a post',
    body:
      'A report is an accusation, not a verdict. The post stays fully visible, labelled "Under Review" once enough independent reports agree there is something to decide.',
  },
  {
    icon: 'users-round',
    title: 'A random jury assembles',
    body:
      'A rotating team of real members is drawn for this one case. Nobody moderates from a permanent seat, and nobody judges a case they are part of.',
  },
  {
    icon: 'file-text',
    title: 'The decision is published',
    body:
      'Keep or remove, the outcome comes with written reasons anyone can read. The decision and its explanation are public, permanently.',
  },
  {
    icon: 'refresh',
    title: 'Anyone can appeal',
    body:
      'Disagree with a decision and you can appeal it. The appeal is decided the same way: a fresh jury, in public, with written reasons.',
  },
];

export function HowItWorksSection() {
  return (
    <Wrap aria-labelledby="landing-how-heading">
      <SectionHeading id="landing-how-heading">From report to verdict</SectionHeading>
      <Steps>
        {STEPS.map((s) => (
          <Step key={s.title}>
            <StepTitle>
              <Icon name={s.icon} size={16} />
              {s.title}
            </StepTitle>
            <StepBody>{s.body}</StepBody>
          </Step>
        ))}
      </Steps>
    </Wrap>
  );
}
